import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, Users, Clock, Euro } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import ChantierTimelineRow from './ChantierTimelineRow';
import { getHoursForDay } from '@/lib/heures-utils';
import type { CalendarAffectation, DayPlanning } from '@/types/calendar';

// Type pour les affectations groupées par chantier
interface GroupedChantier {
  chantierId: string;
  chantierNom: string;
  chantierClient?: string;
  chantierCouleur?: string;
  chantierPictogramme?: string;
  affectations: CalendarAffectation[];
  totalHeures: number;
  totalCout: number;
}

interface ScheduledSectionProps {
  selectedDate: Date;
  planning: DayPlanning | null;
  onEdit: (affectation: CalendarAffectation) => void;
  onDelete: (id: string) => void;
  onCreate: () => void;
}

/**
 * Section "Scheduled" sous le calendrier
 * Affiche le planning du jour sélectionné avec timeline
 * Groupé par chantier avec blocs d'ouvriers horizontaux
 */
const ScheduledSection = ({
  selectedDate,
  planning,
  onEdit,
  onDelete,
  onCreate,
}: ScheduledSectionProps) => {
  const formattedDate = format(selectedDate, 'EEEE d MMMM', { locale: fr });
  const hasAffectations = planning && planning.affectations.length > 0;

  // Regrouper les affectations par chantier
  const groupedByChantier = useMemo<GroupedChantier[]>(() => {
    if (!planning?.affectations) return [];

    const grouped: Record<string, GroupedChantier> = {};

    planning.affectations.forEach((aff) => {
      const chantierId = aff.chantier_id;

      if (!grouped[chantierId]) {
        grouped[chantierId] = {
          chantierId,
          chantierNom: aff.chantier?.nom_chantier || 'Chantier inconnu',
          chantierClient: aff.chantier?.client,
          chantierCouleur: aff.chantier?.couleur,
          chantierPictogramme: aff.chantier?.pictogramme,
          affectations: [],
          totalHeures: 0,
          totalCout: 0,
        };
      }

      grouped[chantierId].affectations.push(aff);

      // Calculer les totaux (utiliser getHoursForDay pour les heures précises)
      const heures = getHoursForDay(aff, selectedDate);
      const tauxHoraire = aff.taux_horaire_specifique ?? aff.membre?.taux_horaire ?? 0;
      const coef = aff.membre?.coefficient_charges || 1.85;
      const cout = heures * tauxHoraire * (coef || 1.85);

      grouped[chantierId].totalHeures += heures;
      grouped[chantierId].totalCout += cout;
    });

    return Object.values(grouped);
  }, [planning?.affectations, selectedDate]);

  return (
    <div className="flex flex-col h-full">
      {/* ═══ HEADER ═══ */}
      <div className="px-6 py-4 border-t border-border/30 flex-shrink-0">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Calendar className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Planifié</h3>
            <p className="text-sm text-muted-foreground capitalize">{formattedDate}</p>
          </div>
        </div>

        {/* Stats rapides */}
        {hasAffectations && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              <span>{planning.membresCount} membre{planning.membresCount > 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              <span>{planning.totalHeures}h</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Euro className="w-4 h-4" />
              <span>{planning.totalCout.toFixed(0)}€</span>
            </div>
          </div>
        )}
      </div>

      {/* ═══ LISTE DES CHANTIERS (groupés) ═══ */}
      <ScrollArea className="flex-1 px-6">
        <div className="py-4 space-y-4">
          {groupedByChantier.length > 0 ? (
            groupedByChantier.map((chantierGroup) => (
              <ChantierTimelineRow
                key={chantierGroup.chantierId}
                chantierGroup={chantierGroup}
                selectedDate={selectedDate}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))
          ) : (
            // État vide
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mb-4">
                <Calendar className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground font-medium mb-1">
                Aucune affectation
              </p>
              <p className="text-sm text-muted-foreground/70">
                Cliquez ci-dessous pour planifier
              </p>
            </div>
          )}
        </div>
      </ScrollArea>

    </div>
  );
};

export default ScheduledSection;
